import * as React from 'react';
import { ONE_YEAR_MILLISECONDS } from 'utils/timeUtils';
import { PatronLocationStateContext } from 'components/patron-location/PatronLocationContext';
import { setSelectedLatLngCookie } from 'utils/patronLocationCookieUtils';
import { usePrevious } from '@tock/hooks';

/**
 * Pure logic component that reacts to PatronLocation and MetroArea changing.
 * This is logically part of PatronLocation but should be installed at the root
 *  of the document, and only once per app.
 */
const PatronLocationStateMonitor: React.FunctionComponent<{}> = () => {
  const {
    currentMetroArea,
    currentPatronLocation,
    ensureMetroAreaRespectsCurrentPatronLocation,
    ensurePatronLocationRespectsCurrentMetroArea,
    initialized,
  } = React.useContext(PatronLocationStateContext);
  const previousMetroArea = usePrevious(currentMetroArea);
  const previousPatronLocation = usePrevious(currentPatronLocation);

  React.useEffect(() => {
    if (!initialized) {
      return;
    }

    if (currentMetroArea && !currentPatronLocation) {
      // Handling no patloc.
      ensurePatronLocationRespectsCurrentMetroArea();
    } else if (
      currentMetroArea &&
      previousMetroArea &&
      currentMetroArea.id !== previousMetroArea.id
    ) {
      // Handling metro change.
      ensurePatronLocationRespectsCurrentMetroArea();
    } else if (
      currentPatronLocation &&
      previousPatronLocation &&
      currentPatronLocation.placeId !== previousPatronLocation.placeId
    ) {
      // Handling patloc change.
      const latlng = `${currentPatronLocation.lat},${currentPatronLocation.lng}`;
      setSelectedLatLngCookie(window?.location?.hostname, latlng, ONE_YEAR_MILLISECONDS);
      ensureMetroAreaRespectsCurrentPatronLocation();
      // Handling current metro area and current patron location are initialized nothing previous has been set
    } else if (currentMetroArea && currentPatronLocation && !previousPatronLocation) {
      ensurePatronLocationRespectsCurrentMetroArea();
    }
  }, [
    currentMetroArea,
    currentPatronLocation,
    ensureMetroAreaRespectsCurrentPatronLocation,
    ensurePatronLocationRespectsCurrentMetroArea,
    initialized,
    previousMetroArea,
    previousPatronLocation,
  ]);

  return null;
};

export default PatronLocationStateMonitor;
